import {
  Badge,
  Button,
  Callout,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Switch,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Textarea,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileStack, Info, Plus } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';

interface DocumentTypeRow {
  id: string;
  code: string;
  name: string;
  agreement: string | null;
  description: string | null;
  preferential: boolean;
  active: boolean;
  sortOrder: number;
  chambersEnabled: number;
  certificatesIssued: number;
}

/**
 * The platform document-type catalogue.
 *
 * New free trade agreements arrive — A-UKFTA, and whatever follows. Until this screen
 * existed the only way to add the matching certificate type was a database seed, which
 * meant a developer and a deployment for what is a piece of reference data.
 *
 * Creating a type here does not make it issuable. A chamber must be accredited for it on
 * its own page, and then configure stamping and numbering, before it can issue one.
 */
export function DocumentTypes() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentTypeRow | null>(null);

  const query = useQuery({
    queryKey: ['document-types-manage'],
    queryFn: () => api.get<DocumentTypeRow[]>('/api/console/platform/document-types/manage'),
  });

  const rows = query.data ?? [];
  const unused = rows.filter((r) => r.active && r.chambersEnabled === 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Document types"
        lede="Every kind of certificate the platform knows how to issue. A new free trade agreement is a new type here — then each chamber is accredited for it separately."
        actions={
          <Button variant="primary" leadingIcon={<Plus className="size-3.5" />} onClick={() => setCreateOpen(true)}>
            Add a document type
          </Button>
        }
      />

      {unused.length > 0 && (
        <Callout
          tone="info"
          icon={<Info className="size-4" />}
          title={`${unused.length} type${unused.length === 1 ? ' is' : 's are'} not enabled for any chamber`}
        >
          {unused.map((r) => r.code).join(', ')} — adding a type to the catalogue does not
          make it issuable. Accredit a chamber for it on that chamber's page, then configure
          its stamping layout and number format.
        </Callout>
      )}

      {query.isLoading ? (
        <Skeleton className="h-72" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<FileStack className="size-5" />}
          title="No document types"
          description="A document type is the kind of certificate an agreement defines — a ChAFTA certificate of origin, a plain certificate of Australian origin."
        />
      ) : (
        <TableShell>
          <Table className="min-w-[860px]">
            <Thead>
              <Tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Agreement</Th>
                <Th numeric>Chambers</Th>
                <Th numeric>Issued</Th>
                <Th>Active</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id} onClick={() => setEditing(row)}>
                  <Td>
                    <CodeChip tone="leaf">{row.code}</CodeChip>
                  </Td>
                  <Td>
                    <span className="font-medium">{row.name}</span>
                    {row.description && (
                      <span className="mt-0.5 block truncate [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        {row.description}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {row.agreement ? (
                      <span className="[font-size:12.5px]">{row.agreement}</span>
                    ) : (
                      <Badge tone="neutral" size="sm">Non-preferential</Badge>
                    )}
                  </Td>
                  <Td numeric>{row.chambersEnabled || <span className="text-[var(--pv-faint)]">—</span>}</Td>
                  <Td numeric>{row.certificatesIssued || <span className="text-[var(--pv-faint)]">—</span>}</Td>
                  <Td>
                    {row.active ? (
                      <Badge tone="ok" size="sm" dot>Active</Badge>
                    ) : (
                      <Badge tone="neutral" size="sm">Retired</Badge>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}

      {createOpen && <DocumentTypeDialog onClose={() => setCreateOpen(false)} />}
      {editing && <DocumentTypeDialog existing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function DocumentTypeDialog({
  existing,
  onClose,
}: {
  existing?: DocumentTypeRow;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(existing?.code ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [agreement, setAgreement] = useState(existing?.agreement ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [preferential, setPreferential] = useState(existing?.preferential ?? true);
  const [sortOrder, setSortOrder] = useState(existing?.sortOrder ?? 100);
  const [active, setActive] = useState(existing?.active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        agreement: agreement || null,
        description: description || null,
        preferential,
        sortOrder,
      };
      return existing
        ? api.patch(`/api/console/platform/document-types/${existing.id}`, { ...body, active })
        : api.post('/api/console/platform/document-types', { ...body, code });
    },
    onSuccess: () => {
      toast.success(
        existing ? 'Document type updated' : `${code} added`,
        existing ? undefined : 'Accredit a chamber for it to make it issuable.',
      );
      void queryClient.invalidateQueries({ queryKey: ['document-types-manage'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-document-types'] });
      onClose();
    },
    onError: (error) =>
      toast.error(
        existing ? 'Could not update the type' : 'Could not add the type',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const inUse = (existing?.chambersEnabled ?? 0) > 0 || (existing?.certificatesIssued ?? 0) > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[38rem]">
        <DialogHeader
          title={existing ? `${existing.code}` : 'Add a document type'}
          description={
            existing
              ? 'The code is fixed. Everything else can change.'
              : 'The kind of certificate an agreement defines. Adding it here does not make it issuable — a chamber is accredited for it separately.'
          }
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field
              label="Code"
              required
              help={
                existing
                  ? 'Permanent. Integrators submit against it, and every certificate issued under it refers to it.'
                  : 'Uppercase letters, digits and underscores. This is what integrators submit — CHAFTA_CO, AUKFTA_CO. It can never be changed.'
              }
            >
              <Input
                value={code}
                disabled={Boolean(existing)}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
                placeholder="AUKFTA_CO"
                className="pv-num"
              />
            </Field>

            <Field label="Name" required help="What operators and integrators see.">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Australia–UK FTA Certificate of Origin"
              />
            </Field>

            <Field label="Agreement" help="Leave blank for a non-preferential certificate.">
              <Input
                value={agreement}
                onChange={(e) => setAgreement(e.target.value)}
                placeholder="Australia–United Kingdom Free Trade Agreement"
              />
            </Field>

            <Field label="Description">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Claims an FTA benefit" help="Preferential origin, rather than a plain certificate.">
                <NativeSelect
                  value={preferential ? 'yes' : 'no'}
                  onChange={(e) => setPreferential(e.target.value === 'yes')}
                >
                  <option value="yes">Yes — preferential</option>
                  <option value="no">No — non-preferential</option>
                </NativeSelect>
              </Field>
              <Field label="Sort order" help="Lower sorts first in every picker.">
                <Input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                />
              </Field>
            </div>

            {existing && (
              <div className="flex items-start justify-between gap-4 rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3">
                <div className="min-w-0">
                  <p className="[font-size:13px] font-medium">Active on the platform</p>
                  <p className="mt-0.5 [font-size:12.5px] text-[var(--pv-muted)]">
                    {inUse
                      ? `Enabled for ${existing.chambersEnabled} chamber(s), ${existing.certificatesIssued} certificate(s) issued. Retiring it is refused while any chamber still has it enabled.`
                      : 'Not in use by any chamber. Safe to retire.'}
                  </p>
                </div>
                <Tooltip
                  content={
                    existing.chambersEnabled > 0
                      ? 'Disable it for each chamber first'
                      : 'Retire this type'
                  }
                >
                  <span>
                    <Switch
                      checked={active}
                      disabled={existing.chambersEnabled > 0}
                      onCheckedChange={setActive}
                      aria-label="Active on the platform"
                    />
                  </span>
                </Tooltip>
              </div>
            )}

            {!existing && (
              <Callout tone="info" icon={<AlertTriangle className="size-4" />} title="The code is permanent">
                Integrators submit against it and every certificate issued under it refers to
                it, so it cannot be changed afterwards — a rename would break live
                integrations and orphan history. Get it right now.
              </Callout>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || (!existing && !code.trim())}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {existing ? 'Save changes' : 'Add document type'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
